/**
 * Careers page initialization
 * Handles job listing interactions and modal popup with AJAX loading,
 * plus the reCAPTCHA v3 handshake for the native (multipart, file-upload)
 * application form submission.
 *
 * NOTE:
 * This file is bundled with other /about pages.
 * It must silently no-op when the careers DOM is not present.
 */

import { orgContactPhrase } from '../../utils/orgContact.js';

/**
 * The application form submits natively (multipart/form-data, for the resume
 * upload) rather than via fetch, so unlike the AJAX-based ContactFixedForm
 * used elsewhere, nothing ever calls grecaptcha.execute() for it. Without a
 * token, RecaptchaService.verifyToken() blocks the submission server-side
 * with "Security verification failed" on every single attempt. Fetch a token
 * and attach it as a hidden field before letting the real submission through.
 */
function initCareersApplicationForm() {
    // Selected structurally (not by `action` or `id`): the form's `action`
    // attribute currently resolves to '/contact' rather than the intended
    // '/about/careers/apply' (a separate, pre-existing bug — see careers.ejs),
    // and its formId is admin-configurable at runtime.
    const form = document.querySelector('.careers-contact-section form');
    if (!form) return;

    // Prevent double-binding (shared bundles / HMR / multiple entries)
    if (form.dataset.recaptchaHandlerInitialized === 'true') return;
    form.dataset.recaptchaHandlerInitialized = 'true';

    const siteKey = form.dataset.recaptchaSiteKey;
    if (!siteKey) return; // reCAPTCHA not configured — let native submission proceed as-is

    function showInlineError(message) {
        const existing = form.parentNode ? form.parentNode.querySelector('.careers-apply-error') : null;
        if (existing) existing.remove();

        const el = document.createElement('div');
        el.className = 'careers-apply-error';
        el.textContent = message;
        el.style.cssText =
            'padding: 15px; margin-top: 15px; background-color: #f8d7da; border: 1px solid #f5c2c7; border-radius: 4px; color: #842029;';

        if (form.parentNode) {
            form.parentNode.insertBefore(el, form.nextSibling);
            el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }

    if (!document.querySelector('script[src*="recaptcha/api.js"]')) {
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
        script.async = true;
        document.head.appendChild(script);
    }

    async function getRecaptchaToken() {
        if (typeof grecaptcha === 'undefined') {
            try {
                await new Promise((resolve, reject) => {
                    const timeout = setTimeout(() => reject(new Error('reCAPTCHA load timeout')), 8000);
                    const poll = setInterval(() => {
                        if (typeof grecaptcha !== 'undefined') {
                            clearInterval(poll);
                            clearTimeout(timeout);
                            resolve();
                        }
                    }, 50);
                });
            } catch {
                return null;
            }
        }

        try {
            return await new Promise((resolve, reject) => {
                grecaptcha.ready(() => {
                    grecaptcha.execute(siteKey, { action: 'careers_apply' }).then(resolve).catch(reject);
                });
            });
        } catch {
            return null;
        }
    }

    form.addEventListener('submit', async function (e) {
        // Already prevented by the partial's own field-validation listener
        // (registered earlier, since it's inline in the page HTML) → bail out
        // and leave the native submission cancelled.
        if (e.defaultPrevented) return;

        e.preventDefault();

        const submitBtn = form.querySelector('button[type="submit"]');
        const originalText = submitBtn ? submitBtn.textContent : '';
        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.textContent = 'Verifying...';
        }

        const token = await getRecaptchaToken();

        if (!token) {
            if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.textContent = originalText;
            }
            showInlineError(
                "We couldn't complete the security check for this form — this is often caused by an ad-blocker or " +
                'privacy extension blocking Google\'s verification script. Try disabling it and resubmitting, or ' +
                'reach us directly at ' + orgContactPhrase() + '.'
            );
            return;
        }

        let tokenField = form.querySelector('input[name="recaptchaToken"]');
        if (!tokenField) {
            tokenField = document.createElement('input');
            tokenField.type = 'hidden';
            tokenField.name = 'recaptchaToken';
            form.appendChild(tokenField);
        }
        tokenField.value = token;

        // HTMLFormElement.submit() does not dispatch a 'submit' event, so this
        // does not re-enter this listener.
        form.submit();
    });
}

function initCareersPage() {
    const jobListings = document.querySelectorAll('.job-listing');
    const modal = document.getElementById('jobModal');
    const modalTitle = document.getElementById('jobModalTitle');
    const modalBody = document.getElementById('jobModalBody');
    const closeBtn = document.getElementById('jobModalClose');

    // Not a careers page → silently no-op
    if (!modal || jobListings.length === 0) {
        return;
    }

    // Prevent double-init
    if (modal.dataset.careersInitialized === 'true') {
        return;
    }
    modal.dataset.careersInitialized = 'true';

    console.log('[Careers] Initializing careers page...');
    console.log('[Careers] Found job listings:', jobListings.length);

    jobListings.forEach((listing) => {
        listing.addEventListener('click', async () => {
            const jobTitle = listing.dataset.jobTitle;
            if (!jobTitle) return;

            // Update modal title
            if (modalTitle) {
                modalTitle.textContent = jobTitle;
            }

            // Show loading state
            if (modalBody) {
                modalBody.innerHTML =
                    '<div class="modal-loading">Loading job description...</div>';
            }

            // Open modal (clear inline display:none so CSS display:flex takes over)
            modal.style.display = '';
            requestAnimationFrame(() => modal.classList.add('modal-open'));

            try {
                const response = await fetch(
                    `/about/careers/api/job-description/${encodeURIComponent(jobTitle)}`
                );

                if (!response.ok) {
                    throw new Error(`Failed to load job description (${response.status})`);
                }

                const data = await response.json();

                if (data.success && data.content) {
                    modalBody.innerHTML = data.content;
                } else {
                    throw new Error(data.message || 'Job description not available');
                }
            } catch (error) {
                console.error('[Careers] Error loading job description:', error);

                if (modalBody) {
                    modalBody.innerHTML = `
            <div class="modal-error">
              <p><strong>Unable to load job description</strong></p>
              <p>${error.message || 'Please try again later or contact us directly.'}</p>
            </div>
          `;
                }
            }
        });
    });

    // Close on button or backdrop click
    function closeModal() {
        modal.classList.remove('modal-open');
        modal.addEventListener('transitionend', () => {
            modal.style.display = 'none';
        }, { once: true });
    }

    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
    });

    console.log('[Careers] Careers page initialized');
}

// Safe DOM-ready init
function initCareers() {
    initCareersPage();
    initCareersApplicationForm();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCareers);
} else {
    initCareers();
}
